import { ActionTree } from 'vuex';
import { CalendarState } from './types';
import { RootState } from '@/store/types';
import api from '@/api';
import * as calendarsRequests from '@/api/requests/calendar/calendars';
import * as calendarSubsRequests from '@/api/requests/calendar/calendar-subscriptions';
import * as eventRequests from '@/api/requests/calendar/events';
import { CalendarRequest, CalendarSubscriptionRequest, EventRequest, FullcalendarQueryParams } from '@/api/requests/calendar/types';
import Event from '@/api/models/event';

export const actions: ActionTree<CalendarState, RootState> = {
    /**
     * Create a new calendar.
     *
     * @param commit
     * @param calendar
     * @returns {Promise<void>}
     */
    async createCalendar({ commit }, calendar: CalendarRequest) {
        const resp = await api.call(calendarsRequests.SAVE(calendar));
        const response = await api.call(calendarSubsRequests.FIND_SUBSCRIBED_CALENDAR(resp.data.data.id));
        commit('addCalendar', response.data.data);
    },

    /**
     * Get fullcalendar resources.
     *
     * @param commit
     * @param getters
     * @param params
     */
    async fetchFullcalendarResources({ commit, getters }, params?: FullcalendarQueryParams) {
        const response = await api.call(calendarsRequests.RESOURCES(params));
        return response.data;
    },

    /**
     * Get fullcalendar events.
     *
     * @param commit
     * @param getters
     * @param params
     */
    async fetchFullcalendarEvents({ commit, getters }, params?: FullcalendarQueryParams) {
        const response = await api.call(calendarsRequests.FULLCALENDAR_EVENTS(params));
        return response.data;
    },

    /**
     * Create a new event, adding it to the calendar.
     *
     * @param commit
     * @param event
     * @returns {Promise<void>}
     */
    async createEvent({ commit }, event: Event) {
        const response = await api.call(eventRequests.SAVE(event.calendarId, event.toRequest()));
        commit('addEvents', response.data.data);
    },

    /**
     * Delete a calendar.
     *
     * @param commit
     * @param calendarId
     */
    async deleteCalendar({ commit }, calendarId: string | number) {
        await api.call(calendarsRequests.DESTROY(calendarId));
        commit('removeCalendar', calendarId);
    },

    /**
     * Delete an event.
     *
     * @param commit
     * @param getters
     * @param eventId
     */
    async deleteEvent({ commit, getters, state }, eventId: string | number) {
        const event = getters.getEventById(eventId);
        await api.call(eventRequests.DESTROY(event.calendarId, event.id));
        commit('removeEvent', event.id);
    },

    /**
     * Fetch all calendars.
     */
    async fetchAvailableCalendars({ commit, getters }, filters) {
        await commit('setCalendarFilters', filters);
        const response = await api.call(calendarsRequests.ALL(getters.calendarQueryParams));
        await commit('setAvailableCalendars', response.data.data);
    },

    /**
     * Fetch all calendars subscribed by user.
     */
    async fetchCalendars({ state, commit }) {
        const response = await api.call(calendarSubsRequests.ALL_SUBSCRIBED_CALENDARS());
        commit('setCalendars', response.data.data);
    },

    /**
     * Fetch all calendar events.
     *
     * @param commit
     * @param getters
     * @param {string|number} calendarId
     * @returns {Promise<void>}
     */
    async fetchCalendarEvents({ commit, state, getters }, calendarId: string | number) {
        const response = await api.call(eventRequests.ALL(calendarId, getters.eventQueryParams));
        commit('addEvents', response.data.data);
    },

    /**
     * Fetch all events with filters.
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param filters
     */
    async fetchEvents({ commit, state, dispatch, getters }, filters) {
        await commit('resetEvents');
        await commit('setEventFilters', filters);
        await Promise.all(state.calendars.map((calendar) => dispatch('fetchCalendarEvents', calendar.id)));
        await Promise.all(getters.companiesCalendars.map((calendar: any) => dispatch('fetchCalendarEvents', calendar.id)));
    },

    /**
     * Fetch the full list of user' calendars and events.
     *
     * @param commit
     * @param dispatch
     */
    async initCalendars({ state, commit, dispatch }) {
        // Reset calendars
        commit('resetCalendars');
        // Reset events
        commit('resetEvents');
        // Reset filters
        commit('resetEventFilters');
        // Fetch calendars
        await dispatch('fetchCalendars');
    },

    /**
     * Move an event to another calendar.
     *
     * @param commit
     * @param getters
     * @param event
     */
    async moveEvent({ commit, getters }, event: Event) {
        if (!event.id) {
            throw new Error('Event id is required to update an event.');
        }
        const original = getters.getEventById(event.id);
        const response = await api.call(eventRequests.MOVE(original.calendarId, event.id, event.calendarId));
        commit('updateEvent', response.data.data);
    },

    /**
     * Subscribe user to the specified calendar.
     *
     * @param commit
     * @param getters
     * @param data
     * @param calendarId
     */
    async subscribeCalendar({ commit, getters }, data: CalendarSubscriptionRequest) {
        if (!! getters.getCalendarById(data.id)) { return; }
        const response = await api.call(calendarSubsRequests.SUBSCRIBE_CALENDAR(data));
        commit('addCalendar', response.data.data);
    },

    /**
     * Subscribe user to the specified calendar.
     *
     * @param commit
     * @param getters
     * @param calendarId
     */
    async unsubscribeCalendar({ commit }, calendarId: string) {
        await api.call(calendarSubsRequests.UNSUBSCRIBE_CALENDAR(calendarId));
        commit('removeCalendar', calendarId);
    },

    /**
     * Update an existing calendar.
     *
     * @param commit
     * @param calendar
     */
    async updateCalendarDetails({ commit }, calendar) {
        await api.call(calendarsRequests.UPDATE(calendar.id, calendar));
        const response = await api.call(calendarSubsRequests.FIND_SUBSCRIBED_CALENDAR(calendar.id));
        commit('updateCalendar', response.data.data);
    },

    /**
     * Update an existing calendar.
     *
     * @param commit
     * @param calendar
     */
    async updateCalendarSubscription({ commit }, calendar) {
        const response = await api.call(calendarSubsRequests.UPDATE_SUBSCRIBED_CALENDAR(calendar.id, calendar));
        commit('updateCalendar', response.data.data);
    },

    /**
     * Update an existing calendar.
     *
     * @param commit
     * @param dispatch
     * @param getters
     * @param event
     */
    async updateEvent({ state, commit, dispatch, getters }, event: Event) {
        if (!event.id) {
            throw new Error('Event id is required to update an event.');
        }
        const original = getters.getEventById(event.id);
        if (original.calendarId !== event.calendarId) {
            await dispatch('moveEvent', event);
        }
        const response = await api.call(eventRequests.UPDATE(event.calendarId, event.id, event.toRequest()));
        commit('updateEvent', response.data.data);
    },
};
